import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Clock, CheckCircle2, BookOpen } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Phase 6 — "Learning Mastery" box on the dashboard Progress section.
 * Purely a read-only view of GET /user/card-mastery-summary (itself a
 * read-only aggregate of the existing Challenge Card `card_mastery` data,
 * same 7-day review rule as FlashSpecsPage — no second mastery calculation
 * happens here). Distinct from "Accuracy by Question Type" (a different,
 * pre-existing weak-questions system) and from formal test results —
 * deliberately its own clearly-labelled box so the three progress metrics
 * are never conflated.
 */
export default function CardReviewSummary() {
  const [summary, setSummary] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/user/card-mastery-summary`)
      .then((r) => setSummary(r.data))
      .catch(() => setSummary(null))
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded) return null;

  // Endpoint unreachable — an honest fallback, never a guessed/fabricated count.
  if (!summary) {
    return (
      <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]" data-testid="card-review-summary">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Clock size={16} className="text-[hsl(var(--primary))]" />
            Challenge Card Review
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-[hsl(var(--muted-foreground))]" data-testid="card-review-unavailable">
            Review status is unavailable right now.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (summary.total_tracked === 0) {
    return (
      <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]" data-testid="card-review-summary">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Clock size={16} className="text-[hsl(var(--primary))]" />
            Challenge Card Review
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-[hsl(var(--muted-foreground))]" data-testid="card-review-empty">
            No Challenge Cards tested yet. Take a card challenge in FlashSpecs to start tracking review status.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]" data-testid="card-review-summary">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Clock size={16} className="text-[hsl(var(--primary))]" />
          Challenge Card Review
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-3">
          <div className="flex-1 rounded-lg bg-[hsl(var(--secondary))] p-3 text-center" data-testid="cards-due-count">
            <p className="text-2xl font-bold text-red-400">{summary.due_count}</p>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">Due for Review</p>
          </div>
          <div className="flex-1 rounded-lg bg-[hsl(var(--secondary))] p-3 text-center" data-testid="cards-mastered-count">
            <p className="text-2xl font-bold text-green-500">{summary.mastered_count}</p>
            <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">Mastered</p>
          </div>
        </div>

        {summary.due_for_review.length > 0 && (
          <div data-testid="due-for-review-list">
            <p className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1.5">Due Now</p>
            <div className="space-y-1.5">
              {summary.due_for_review.map((c) => (
                <Link
                  key={c.cocktail_id}
                  to={`/flashspecs?deck=${encodeURIComponent(c.deck_name || "")}&card=${encodeURIComponent(c.cocktail_id)}`}
                  className="flex items-center justify-between px-2.5 py-1.5 rounded-lg bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--accent))] transition-colors text-sm"
                  data-testid={`due-card-${c.cocktail_id}`}
                >
                  <span className="truncate">{c.cocktail_name}</span>
                  <span className="text-red-400 text-xs shrink-0 ml-2">Practice</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {summary.recently_mastered.length > 0 && (
          <div data-testid="recently-mastered-list">
            <p className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1.5">Recently Mastered</p>
            <div className="space-y-1.5">
              {summary.recently_mastered.map((c) => (
                <div
                  key={c.cocktail_id}
                  className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-green-500/5 border border-green-500/15 text-sm"
                  data-testid={`mastered-card-${c.cocktail_id}`}
                >
                  <CheckCircle2 size={14} className="text-green-500 shrink-0" />
                  <span className="truncate">{c.cocktail_name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {summary.due_count === 0 && summary.due_for_review.length === 0 && (
          <Link
            to="/flashspecs"
            className="flex items-center gap-1.5 text-xs text-[hsl(var(--primary))] font-medium hover:gap-2 transition-all"
            data-testid="card-review-all-clear"
          >
            <BookOpen size={13} /> All caught up — browse FlashSpecs for more
          </Link>
        )}
      </CardContent>
    </Card>
  );
}
